import { useState } from "react";
import { ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { PUBLIC_WEB_ORIGIN } from "../lib/backendBase";

/**
 * Share-Truck button. Uses the device-native share sheet (Web Share API)
 * on iOS/Android, falls back to clipboard on desktop.
 *
 * Strategy:
 *   1. Hit `/api/truck/{id}/share.json` to grab the freshest title/blurb
 *      so the share text reflects the truck's current event ("Open now"
 *      vs "Wed 11am-2pm"). This is fast (~100ms) and beats any client-side
 *      formatting we could do.
 *   2. Call `navigator.share({ title, text, url })` if available — that's
 *      what gives us the iOS/Android native sheet.
 *   3. Fall back to `navigator.clipboard.writeText(url)` + toast.
 *
 * The shared URL is ALWAYS the backend's `/api/truck/{id}/share` endpoint,
 * not the SPA route, because that's the URL crawlers (iMessage, WhatsApp,
 * Twitter) need to fetch to read OG meta tags. Real users tapping the
 * link get a 50ms JS redirect into the SPA.
 */
export default function ShareButton({ truckId, truckName, compact = false }) {
  const [busy, setBusy] = useState(false);

  const click = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      // Pull the canonical share metadata (always succeeds — backend
      // returns a fallback even for unknown ids, so we never block).
      let meta;
      try {
        const { data } = await api.get(`/truck/${truckId}/share.json`);
        meta = data;
      } catch {
        // Construct a sensible fallback so the share still works offline-ish
        meta = {
          title: truckName || "TruckTrackr",
          description: "Found on TruckTrackr",
          share_url: `${PUBLIC_WEB_ORIGIN}/api/truck/${truckId}/share`,
        };
      }
      const shareUrl = meta.share_url;
      const title = meta.title || truckName || "TruckTrackr";
      const text = `${meta.description || "Check out this truck"} — Found on TruckTrackr 👀`;

      // Web Share API — gives us the iOS/Android native share sheet.
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ title, text, url: shareUrl });
          // Native sheet handles its own UI; no toast needed.
          return;
        } catch (err) {
          // User dismissed the sheet — silent. Other errors fall through
          // to the clipboard path so the action never feels broken.
          if (err && err.name === "AbortError") return;
        }
      }

      // Fallback: copy to clipboard.
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied — paste anywhere");
      } catch {
        // Last-ditch fallback for ancient browsers without clipboard API.
        window.prompt("Copy this link:", shareUrl);
      }
    } finally {
      setBusy(false);
    }
  };

  const base =
    "inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors select-none";
  const sizing = compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-[13px]";
  const cls =
    "bg-background border border-border text-foreground hover:border-primary/50 hover:text-primary";

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      className={`${base} ${sizing} ${cls} disabled:opacity-60`}
      data-testid="share-truck-btn"
      aria-label={`Share ${truckName || "this truck"}`}
    >
      <ShareNetwork size={compact ? 12 : 14} weight="bold" aria-hidden="true" />
      {busy ? "Sharing…" : "Share"}
    </button>
  );
}
